/**
 * Achievements Service
 * 
 * Manages user achievements and unlocks
 */

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { UserStats } from './userStats';
import type { Trip } from './trips';

export type AchievementId =
  // Explorer Achievements
  | 'first_steps'
  | 'country_collector_5'
  | 'country_collector_10'
  | 'country_collector_25'
  | 'country_collector_50'
  | 'country_collector_100'
  | 'city_explorer_10'
  | 'city_explorer_25'
  | 'city_explorer_50'
  | 'city_explorer_100'
  | 'continent_hopper'
  | 'weekend_warrior'
  | 'month_master_3'
  | 'month_master_6'
  | 'month_master_12'
  // Landmark Achievements
  | 'landmark_hunter_10'
  | 'landmark_hunter_25'
  | 'landmark_hunter_50'
  | 'world_wonder_seeker'
  | 'icon_collector'
  // Travel Style Achievements
  | 'solo_adventurer'
  | 'family_explorer'
  | 'business_traveler'
  | 'budget_master'
  | 'planning_pro'
  // Milestone Achievements
  | 'days_abroad_100'
  | 'days_abroad_365'
  | 'miles_traveled_10000';

export type Achievement = {
  id: AchievementId;
  name: string;
  description: string;
  category: 'explorer' | 'landmark' | 'style' | 'milestone';
  icon: string;
  tier?: 'bronze' | 'silver' | 'gold' | 'platinum';
  target: number;
  progress: number;
  unlocked: boolean;
  unlockedAt?: Date;
};

export type AchievementProgress = {
  achievementId: AchievementId;
  progress: number;
  target: number;
  unlocked: boolean;
  unlockedAt?: Date;
};

type FirestoreAchievementProgress = {
  achievementId: string;
  progress: number;
  target: number;
  unlocked: boolean;
  unlockedAt?: Timestamp | Date;
};

type FirestoreUserAchievements = {
  userId: string;
  achievements: FirestoreAchievementProgress[];
  unlockedCount: number;
  lastUpdated: Timestamp | Date;
};

/**
 * Achievement definitions
 */
export const ACHIEVEMENT_DEFINITIONS: Record<AchievementId, Omit<Achievement, 'progress' | 'unlocked' | 'unlockedAt'>> = {
  // Explorer Achievements
  first_steps: {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Complete your first trip',
    category: 'explorer',
    icon: 'footsteps',
    target: 1,
  },
  country_collector_5: {
    id: 'country_collector_5',
    name: 'Country Collector',
    description: 'Visit 5 countries',
    category: 'explorer',
    icon: 'globe',
    tier: 'bronze',
    target: 5,
  },
  country_collector_10: {
    id: 'country_collector_10',
    name: 'Country Collector',
    description: 'Visit 10 countries',
    category: 'explorer',
    icon: 'globe',
    tier: 'silver',
    target: 10,
  },
  country_collector_25: {
    id: 'country_collector_25',
    name: 'Country Collector',
    description: 'Visit 25 countries',
    category: 'explorer',
    icon: 'globe',
    tier: 'gold',
    target: 25,
  },
  country_collector_50: {
    id: 'country_collector_50',
    name: 'Country Collector',
    description: 'Visit 50 countries',
    category: 'explorer',
    icon: 'globe',
    tier: 'platinum',
    target: 50,
  },
  country_collector_100: {
    id: 'country_collector_100',
    name: 'World Traveler',
    description: 'Visit 100 countries',
    category: 'explorer',
    icon: 'globe',
    tier: 'platinum',
    target: 100,
  },
  city_explorer_10: {
    id: 'city_explorer_10',
    name: 'City Explorer',
    description: 'Visit 10 cities',
    category: 'explorer',
    icon: 'business',
    tier: 'bronze',
    target: 10,
  },
  city_explorer_25: {
    id: 'city_explorer_25',
    name: 'City Explorer',
    description: 'Visit 25 cities',
    category: 'explorer',
    icon: 'business',
    tier: 'silver',
    target: 25,
  },
  city_explorer_50: {
    id: 'city_explorer_50',
    name: 'City Explorer',
    description: 'Visit 50 cities',
    category: 'explorer',
    icon: 'business',
    tier: 'gold',
    target: 50,
  },
  city_explorer_100: {
    id: 'city_explorer_100',
    name: 'City Explorer',
    description: 'Visit 100 cities',
    category: 'explorer',
    icon: 'business',
    tier: 'platinum',
    target: 100,
  },
  continent_hopper: {
    id: 'continent_hopper',
    name: 'Continent Hopper',
    description: 'Visit all 7 continents',
    category: 'explorer',
    icon: 'earth',
    tier: 'platinum',
    target: 7,
  },
  weekend_warrior: {
    id: 'weekend_warrior',
    name: 'Weekend Warrior',
    description: 'Take 5 weekend trips',
    category: 'explorer',
    icon: 'calendar',
    tier: 'bronze',
    target: 5,
  },
  month_master_3: {
    id: 'month_master_3',
    name: 'Month Master',
    description: 'Travel every month for 3 months',
    category: 'explorer',
    icon: 'calendar',
    tier: 'bronze',
    target: 3,
  },
  month_master_6: {
    id: 'month_master_6',
    name: 'Month Master',
    description: 'Travel every month for 6 months',
    category: 'explorer',
    icon: 'calendar',
    tier: 'silver',
    target: 6,
  },
  month_master_12: {
    id: 'month_master_12',
    name: 'Month Master',
    description: 'Travel every month for 12 months',
    category: 'explorer',
    icon: 'calendar',
    tier: 'gold',
    target: 12,
  },
  // Landmark Achievements
  landmark_hunter_10: {
    id: 'landmark_hunter_10',
    name: 'Landmark Hunter',
    description: 'Visit 10 landmarks',
    category: 'landmark',
    icon: 'location',
    tier: 'bronze',
    target: 10,
  },
  landmark_hunter_25: {
    id: 'landmark_hunter_25',
    name: 'Landmark Hunter',
    description: 'Visit 25 landmarks',
    category: 'landmark',
    icon: 'location',
    tier: 'silver',
    target: 25,
  },
  landmark_hunter_50: {
    id: 'landmark_hunter_50',
    name: 'Landmark Hunter',
    description: 'Visit 50 landmarks',
    category: 'landmark',
    icon: 'location',
    tier: 'gold',
    target: 50,
  },
  world_wonder_seeker: {
    id: 'world_wonder_seeker',
    name: 'World Wonder Seeker',
    description: 'Visit UNESCO World Heritage sites',
    category: 'landmark',
    icon: 'star',
    tier: 'gold',
    target: 1,
  },
  icon_collector: {
    id: 'icon_collector',
    name: 'Icon Collector',
    description: 'Visit famous landmarks',
    category: 'landmark',
    icon: 'camera',
    tier: 'silver',
    target: 5,
  },
  // Travel Style Achievements
  solo_adventurer: {
    id: 'solo_adventurer',
    name: 'Solo Adventurer',
    description: 'Complete 5 solo trips',
    category: 'style',
    icon: 'person',
    tier: 'bronze',
    target: 5,
  },
  family_explorer: {
    id: 'family_explorer',
    name: 'Family Explorer',
    description: 'Complete 5 family trips',
    category: 'style',
    icon: 'people',
    tier: 'bronze',
    target: 5,
  },
  business_traveler: {
    id: 'business_traveler',
    name: 'Business Traveler',
    description: 'Complete 10 business trips',
    category: 'style',
    icon: 'briefcase',
    tier: 'silver',
    target: 10,
  },
  budget_master: {
    id: 'budget_master',
    name: 'Budget Master',
    description: 'Stay under budget on 5 trips',
    category: 'style',
    icon: 'wallet',
    tier: 'silver',
    target: 5,
  },
  planning_pro: {
    id: 'planning_pro',
    name: 'Planning Pro',
    description: 'Create 10 trips with full itinerary',
    category: 'style',
    icon: 'checkmark-circle',
    tier: 'gold',
    target: 10,
  },
  // Milestone Achievements
  days_abroad_100: {
    id: 'days_abroad_100',
    name: '100 Days Abroad',
    description: 'Accumulate 100 days of travel',
    category: 'milestone',
    icon: 'calendar',
    tier: 'silver',
    target: 100,
  },
  days_abroad_365: {
    id: 'days_abroad_365',
    name: '365 Days',
    description: 'Travel for a full year (cumulative)',
    category: 'milestone',
    icon: 'calendar',
    tier: 'platinum',
    target: 365,
  },
  miles_traveled_10000: {
    id: 'miles_traveled_10000',
    name: '10,000 Miles',
    description: 'Track distance traveled',
    category: 'milestone',
    icon: 'airplane',
    tier: 'gold',
    target: 10000,
  },
};

/**
 * Calculate achievement progress from user stats and trips
 */
export function calculateAchievementProgress(
  stats: UserStats,
  trips: Trip[]
): AchievementProgress[] {
  const progress: AchievementProgress[] = [];
  
  // Explorer achievements
  const countryCount = stats.countriesVisited.length;
  const cityCount = stats.citiesVisited.length;
  const continentCount = stats.continentsVisited.length;
  
  // Country collector achievements
  progress.push({
    achievementId: 'country_collector_5',
    progress: Math.min(countryCount, 5),
    target: 5,
    unlocked: countryCount >= 5,
  });
  progress.push({
    achievementId: 'country_collector_10',
    progress: Math.min(countryCount, 10),
    target: 10,
    unlocked: countryCount >= 10,
  });
  progress.push({
    achievementId: 'country_collector_25',
    progress: Math.min(countryCount, 25),
    target: 25,
    unlocked: countryCount >= 25,
  });
  progress.push({
    achievementId: 'country_collector_50',
    progress: Math.min(countryCount, 50),
    target: 50,
    unlocked: countryCount >= 50,
  });
  progress.push({
    achievementId: 'country_collector_100',
    progress: Math.min(countryCount, 100),
    target: 100,
    unlocked: countryCount >= 100,
  });
  
  // City explorer achievements
  progress.push({
    achievementId: 'city_explorer_10',
    progress: Math.min(cityCount, 10),
    target: 10,
    unlocked: cityCount >= 10,
  });
  progress.push({
    achievementId: 'city_explorer_25',
    progress: Math.min(cityCount, 25),
    target: 25,
    unlocked: cityCount >= 25,
  });
  progress.push({
    achievementId: 'city_explorer_50',
    progress: Math.min(cityCount, 50),
    target: 50,
    unlocked: cityCount >= 50,
  });
  progress.push({
    achievementId: 'city_explorer_100',
    progress: Math.min(cityCount, 100),
    target: 100,
    unlocked: cityCount >= 100,
  });
  
  // Continent hopper
  progress.push({
    achievementId: 'continent_hopper',
    progress: Math.min(continentCount, 7),
    target: 7,
    unlocked: continentCount >= 7,
  });
  
  // First steps
  progress.push({
    achievementId: 'first_steps',
    progress: trips.length > 0 ? 1 : 0,
    target: 1,
    unlocked: trips.length > 0,
  });
  
  // Landmark achievements
  const landmarkCount = stats.landmarksVisited.length;
  progress.push({
    achievementId: 'landmark_hunter_10',
    progress: Math.min(landmarkCount, 10),
    target: 10,
    unlocked: landmarkCount >= 10,
  });
  progress.push({
    achievementId: 'landmark_hunter_25',
    progress: Math.min(landmarkCount, 25),
    target: 25,
    unlocked: landmarkCount >= 25,
  });
  progress.push({
    achievementId: 'landmark_hunter_50',
    progress: Math.min(landmarkCount, 50),
    target: 50,
    unlocked: landmarkCount >= 50,
  });
  
  // Days abroad achievements
  progress.push({
    achievementId: 'days_abroad_100',
    progress: Math.min(stats.totalTripDays, 100),
    target: 100,
    unlocked: stats.totalTripDays >= 100,
  });
  progress.push({
    achievementId: 'days_abroad_365',
    progress: Math.min(stats.totalTripDays, 365),
    target: 365,
    unlocked: stats.totalTripDays >= 365,
  });
  
  // Travel style achievements (simplified - would need trip type data)
  const soloTrips = trips.filter(t => t.status === 'completed').length; // Simplified
  progress.push({
    achievementId: 'solo_adventurer',
    progress: Math.min(soloTrips, 5),
    target: 5,
    unlocked: soloTrips >= 5,
  });
  
  // Weekend warrior (trips that are 2-3 days)
  const weekendTrips = trips.filter(t => {
    const days = Math.ceil((t.endDate.getTime() - t.startDate.getTime()) / (1000 * 60 * 60 * 24));
    return days >= 2 && days <= 3;
  }).length;
  progress.push({
    achievementId: 'weekend_warrior',
    progress: Math.min(weekendTrips, 5),
    target: 5,
    unlocked: weekendTrips >= 5,
  });
  
  // Month master (based on streak)
  progress.push({
    achievementId: 'month_master_3',
    progress: Math.min(stats.currentStreak, 3),
    target: 3,
    unlocked: stats.currentStreak >= 3,
  });
  progress.push({
    achievementId: 'month_master_6',
    progress: Math.min(stats.currentStreak, 6),
    target: 6,
    unlocked: stats.currentStreak >= 6,
  });
  progress.push({
    achievementId: 'month_master_12',
    progress: Math.min(stats.currentStreak, 12),
    target: 12,
    unlocked: stats.currentStreak >= 12,
  });
  
  return progress;
}

/**
 * Get user achievements
 */
export async function getUserAchievements(userId: string): Promise<AchievementProgress[]> {
  try {
    const achievementsRef = doc(db, 'achievements', userId);
    const snapshot = await getDoc(achievementsRef);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const data = snapshot.data() as FirestoreUserAchievements;
    return data.achievements.map(a => ({
      achievementId: a.achievementId as AchievementId,
      progress: a.progress,
      target: a.target,
      unlocked: a.unlocked,
      unlockedAt: a.unlockedAt instanceof Timestamp ? a.unlockedAt.toDate() : a.unlockedAt ? new Date(a.unlockedAt) : undefined,
    }));
  } catch (error) {
    console.error('[achievements] Error fetching achievements:', error);
    return [];
  }
}

/**
 * Save user achievements
 */
export async function saveUserAchievements(
  userId: string,
  achievements: AchievementProgress[]
): Promise<AchievementProgress[]> {
  try {
    const achievementsRef = doc(db, 'achievements', userId);
    
    const unlockedCount = achievements.filter(a => a.unlocked).length;
    const newlyUnlocked = achievements.filter(a => a.unlocked && !a.unlockedAt);
    
    // Set unlockedAt for newly unlocked achievements
    const now = new Date();
    const updatedAchievements = achievements.map(a => {
      if (a.unlocked && !a.unlockedAt) {
        return { ...a, unlockedAt: now };
      }
      return a;
    });
    
    const firestoreAchievements: FirestoreUserAchievements = {
      userId,
      achievements: updatedAchievements.map((a) => {
        const baseAchievement: FirestoreAchievementProgress = {
          achievementId: a.achievementId,
          progress: a.progress,
          target: a.target,
          unlocked: a.unlocked,
        };
        // Firestore rejects undefined values in nested objects/arrays.
        if (a.unlockedAt) {
          baseAchievement.unlockedAt = Timestamp.fromDate(a.unlockedAt);
        }
        return baseAchievement;
      }),
      unlockedCount,
      lastUpdated: serverTimestamp() as Timestamp,
    };
    
    await setDoc(achievementsRef, firestoreAchievements, { merge: true });
    
    // Return newly unlocked achievements for celebration
    return newlyUnlocked;
  } catch (error) {
    console.error('[achievements] Error saving achievements:', error);
    throw error;
  }
}

/**
 * Check and update achievements for a user
 */
export async function checkAndUpdateAchievements(
  userId: string,
  stats: UserStats,
  trips: Trip[]
): Promise<AchievementProgress[]> {
  const progress = calculateAchievementProgress(stats, trips);
  await saveUserAchievements(userId, progress);
  return progress;
}

